import { useCallback, useState } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { LegacyColumnConfig } from '../types';

export interface ActiveFilter {
  columnKey: string;
  values: unknown[];
}

export interface FilterModalColumn {
  key: string;
  label: string;
  config: LegacyColumnConfig;
}

interface UseClientFilterResult {
  columnFilters: ColumnFiltersState;
  setColumnFilter: (columnKey: string, values: unknown[]) => void;
  clearColumnFilter: (columnKey: string) => void;
  clearAllFilters: () => void;
  activeFilters: ActiveFilter[];
  filterValues: Record<string, unknown[]>;
  isFilterModalOpen: boolean;
  filterModalColumn: FilterModalColumn | null;
  openFilterModal: (column: FilterModalColumn) => void;
  closeFilterModal: () => void;
  columnHasFilter: (columnKey: string) => boolean;
}

/**
 * v2: parseObject columns now match on the parsed value, not the raw one — bumped so a filter
 * saved under the old schema misses (fails open) instead of comparing stale raw values and
 * silently hiding every row.
 */
const sessionKey = (tableKey: string | undefined) => `filter_v2_${tableKey ?? ''}_${window.location.pathname}`;

const loadFiltersFromSession = (tableKey: string | undefined): ColumnFiltersState => {
  try {
    const raw = window.sessionStorage.getItem(sessionKey(tableKey));
    return raw ? (JSON.parse(raw) as ColumnFiltersState) : [];
  } catch {
    return [];
  }
};

const saveFiltersToSession = (tableKey: string | undefined, filters: ColumnFiltersState) => {
  window.sessionStorage.setItem(sessionKey(tableKey), JSON.stringify(filters));
};

export const useClientFilter = (tableKey: string | undefined, canPersist?: boolean): UseClientFilterResult => {
  // Persistence requires an explicit tableKey (canPersist = Boolean(tableKey), see
  // Table/index.tsx) — tableKey is a stable table-*type* identity, not proof of being the only
  // table on the page, so the session key always includes the pathname too.
  const persistenceEnabled = Boolean(canPersist);

  const [columnFilters, setColumnFiltersState] = useState<ColumnFiltersState>(() =>
    persistenceEnabled ? loadFiltersFromSession(tableKey) : []
  );

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterModalColumn, setFilterModalColumn] = useState<FilterModalColumn | null>(null);

  const persist = useCallback(
    (filters: ColumnFiltersState) => {
      if (persistenceEnabled) saveFiltersToSession(tableKey, filters);
    },
    [persistenceEnabled, tableKey]
  );

  const setColumnFilter = useCallback(
    (columnKey: string, values: unknown[]) => {
      setColumnFiltersState((prev) => {
        const without = prev.filter((f) => f.id !== columnKey);
        const next = values.length === 0 ? without : [...without, { id: columnKey, value: values }];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const clearColumnFilter = useCallback(
    (columnKey: string) => {
      setColumnFiltersState((prev) => {
        const next = prev.filter((f) => f.id !== columnKey);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const clearAllFilters = useCallback(() => {
    setColumnFiltersState([]);
    persist([]);
  }, [persist]);

  const openFilterModal = useCallback((column: FilterModalColumn) => {
    setFilterModalColumn(column);
    setIsFilterModalOpen(true);
  }, []);

  const closeFilterModal = useCallback(() => {
    setIsFilterModalOpen(false);
    setFilterModalColumn(null);
  }, []);

  const columnHasFilter = useCallback(
    (columnKey: string) => columnFilters.some((f) => f.id === columnKey),
    [columnFilters]
  );

  const activeFilters: ActiveFilter[] = columnFilters.map((f) => ({
    columnKey: f.id,
    values: f.value as unknown[],
  }));

  const filterValues = activeFilters.reduce<Record<string, unknown[]>>((acc, f) => {
    acc[f.columnKey] = f.values;
    return acc;
  }, {});

  return {
    columnFilters,
    setColumnFilter,
    clearColumnFilter,
    clearAllFilters,
    activeFilters,
    filterValues,
    isFilterModalOpen,
    filterModalColumn,
    openFilterModal,
    closeFilterModal,
    columnHasFilter,
  };
};
